import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, MapPin, Phone, ArrowUpRight, Grid, List, ChevronDown, Lock } from 'lucide-react';
import { vendorService } from '../services/api';
const slugify = (text) => {
  if (!text) return '';
  return text
    .toString()
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const Listing = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const base = isLocal ? 'http://localhost:8080' : '';
    if (url.startsWith('/listings/') || url.startsWith('/static/')) return `${base}${url}`;
    return `${base}/listings${url.startsWith('/') ? '' : '/'}${url}`;
  };
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Filter states
  const [selectedState, setSelectedState] = useState(searchParams.get('state') || '');
  const [selectedCity, setSelectedCity] = useState(searchParams.get('city') || '');
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('categoryId') || '');
  const [displayedCategory, setDisplayedCategory] = useState(searchParams.get('categoryId') || '');
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [showBrandsFilter, setShowBrandsFilter] = useState(false);

  // Dropdown list states
  const [states, setStates] = useState([]);
  const [citiesMap, setCitiesMap] = useState({});
  const [categories, setCategories] = useState([]);

  // Listings data states
  const [listings, setListings] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(24);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTrigger, setSearchTrigger] = useState(0);

  // Sync URL search parameters with filter state on changes
  useEffect(() => {
    const stateParam = searchParams.get('state') || '';
    const cityParam = searchParams.get('city') || '';
    const categoryParam = searchParams.get('categoryId') || '';
    const tagParam = searchParams.get('tagId') || '';
    const tagsParam = searchParams.get('tagIds') || '';
    const brandParam = searchParams.get('brandId') || '';
    const brandsParam = searchParams.get('brandIds') || '';
    const searchNameParam = searchParams.get('searchName') || '';

    setSelectedState(stateParam);
    setSelectedCity(cityParam);

    // Only set category and tags from URL directly if searchName is not specified.
    // If searchName is specified, it will be resolved once categories are loaded.
    if (!searchNameParam) {
      setSelectedCategory(categoryParam);
      setDisplayedCategory(categoryParam);
      if (tagsParam) {
        setSelectedTags(tagsParam.split(',').filter(Boolean));
      } else if (tagParam) {
        setSelectedTags([tagParam]);
      } else {
        setSelectedTags([]);
      }

      if (brandsParam) {
        setSelectedBrands(brandsParam.split(',').filter(Boolean));
      } else if (brandParam) {
        setSelectedBrands([brandParam]);
      } else {
        setSelectedBrands([]);
      }
    }

    setCurrentPage(0);
    setSearchTrigger(prev => prev + 1);
  }, [searchParams]);

  // Resolve searchName parameter to actual categoryId or tagId when categories are loaded or param changes
  useEffect(() => {
    const searchNameParam = searchParams.get('searchName') || '';
    if (!searchNameParam || categories.length === 0) return;

    const query = searchNameParam.toLowerCase().trim();

    // 1. Try to find a category that matches the name
    const matchedCategory = categories.find(
      cat => cat.name.toLowerCase().trim() === query
    );

    if (matchedCategory) {
      setSelectedCategory(String(matchedCategory.id));
      setDisplayedCategory(String(matchedCategory.id));
      setSelectedTags([]); // Clear tags since we selected a whole category
      setSearchTrigger(prev => prev + 1);
      return;
    }

    // 2. Try to find a tag that matches the name under any category
    let foundCategory = null;
    let foundTag = null;

    for (const cat of categories) {
      if (cat.tags) {
        const matchedTag = cat.tags.find(
          tag => tag.name.toLowerCase().trim() === query
        );
        if (matchedTag) {
          foundCategory = cat;
          foundTag = matchedTag;
          break;
        }
      }
    }

    if (foundTag && foundCategory) {
      setSelectedCategory(String(foundCategory.id));
      setDisplayedCategory(String(foundCategory.id));
      setSelectedTags([String(foundTag.id)]);
      setSearchTrigger(prev => prev + 1);
    }
  }, [searchParams, categories]);

  // Load locations and categories on mount
  useEffect(() => {
    const loadMetadata = async () => {
      try {
        const locRes = await vendorService.getPublicLocations();
        if (locRes.success && locRes.data) {
          setStates(locRes.data.states || []);
          setCitiesMap(locRes.data.cities || {});
        }

        const catRes = await vendorService.getCategories();
        if (catRes.success && catRes.data) {
          setCategories(catRes.data || []);
        }
      } catch (err) {
        console.error("Error loading listing metadata:", err);
      }
    };
    loadMetadata();
  }, []);

  // Fetch listings when filters or page changes
  useEffect(() => {
    const fetchListings = async () => {
      setLoading(true);
      try {
        const filters = {};
        if (selectedState) filters.state = selectedState;
        if (selectedCity) filters.city = selectedCity;
        if (selectedCategory) filters.categoryId = selectedCategory;
        if (selectedTags && selectedTags.length > 0) {
          filters.tagIds = selectedTags.join(',');
        }
        if (selectedBrands && selectedBrands.length > 0) {
          filters.brandIds = selectedBrands.join(',');
        }

        const res = await vendorService.getPublicListings(filters, currentPage, pageSize);
        if (res.success && res.data) {
          setListings(res.data.listings || []);
          setTotalItems(res.data.totalItems || 0);
          setTotalPages(res.data.totalPages || 0);
        } else {
          setListings([]);
          setTotalItems(0);
          setTotalPages(0);
        }
      } catch (err) {
        console.error("Error fetching public listings:", err);
        setListings([]);
        setTotalItems(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    };
    fetchListings();
  }, [currentPage, pageSize, searchTrigger]);

  const handleSearch = (e) => {
    e.preventDefault();
    setDisplayedCategory(selectedCategory);
    setCurrentPage(0);
    setSearchTrigger(prev => prev + 1);
  };

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setSelectedCity(''); // Reset city selection
  };

  const handleCityChange = (e) => {
    const city = e.target.value;
    setSelectedCity(city);
    if (city) {
      for (const state in citiesMap) {
        if (citiesMap[state].includes(city)) {
          setSelectedState(state);
          break;
        }
      }
    }
  };

  const handleCategoryChange = (val) => {
    setSelectedCategory(val);
    setSelectedTags([]); // Reset tag selection
    setSelectedBrands([]); // Reset brand selection
    setShowBrandsFilter(false); // Reset toggle state
  };

  const handleTagClick = (tagId) => {
    let newTags = [];
    if (!tagId) {
      newTags = [];
    } else {
      const strId = String(tagId);
      if (selectedTags.includes(strId)) {
        newTags = selectedTags.filter(id => id !== strId);
      } else {
        newTags = [...selectedTags, strId];
      }
    }
    setSelectedTags(newTags);

    setCurrentPage(0);
    setSearchTrigger(prev => prev + 1);
  };

  const handleBrandClick = (brandId) => {
    if (!brandId) {
      setSelectedBrands([]);
    } else {
      setSelectedBrands(prev => {
        const strId = String(brandId);
        if (prev.includes(strId)) {
          return prev.filter(id => id !== strId);
        } else {
          return [...prev, strId];
        }
      });
    }
    setCurrentPage(0);
    setSearchTrigger(prev => prev + 1);
  };


  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(0);

      let start = Math.max(1, currentPage - 1);
      let end = Math.min(totalPages - 2, currentPage + 1);

      if (currentPage <= 1) {
        end = 3;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 4;
      }

      if (start > 1) {
        pages.push('ellipsis1');
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 2) {
        pages.push('ellipsis2');
      }

      pages.push(totalPages - 1);
    }
    return pages;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Get available cities for selected state
  const availableCities = selectedState
    ? (citiesMap[selectedState] || [])
    : Array.from(new Set(Object.values(citiesMap).flat())).sort();

  const activeCategory = categories.find(cat => String(cat.id) === String(displayedCategory));
  const categoryTags = activeCategory && activeCategory.tags
    ? activeCategory.tags.filter(t => t.isActive)
    : [];
  const categoryBrands = activeCategory && activeCategory.brands
    ? activeCategory.brands.filter(b => b.isActive)
    : [];

  return (
    <div className="bg-[#f8f9fa] min-h-screen pt-28 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Desktop Search Section (Matches original desktop look exactly) */}
        <form
          onSubmit={handleSearch}
          className="hidden md:flex bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-8 flex-row gap-4 items-center justify-between"
        >
          {/* State Select */}
          <div className="w-1/4">
            <select
              value={selectedState}
              onChange={handleStateChange}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer"
            >
              <option value="">State</option>
              {states.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
          </div>

          {/* City Select */}
          <div className="w-1/4">
            <select
              value={selectedCity}
              onChange={handleCityChange}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer"
            >
              <option value="">City</option>
              {availableCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>
          </div>

          {/* Category Select */}
          <div className="w-1/4">
            <select
              value={selectedCategory}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#2ab391] text-gray-700 cursor-pointer"
            >
              <option value="">Category</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          {/* Search & Reset Action Buttons */}
          <div className="flex gap-2 w-auto flex-shrink-0">
            <button
              type="submit"
              className="bg-[#2ab391] hover:bg-[#23977a] text-white p-3 rounded-xl flex items-center justify-center gap-2 font-medium px-8 transition-colors shadow-sm cursor-pointer"
            >
              <Search size={18} />
            </button>
          </div>
        </form>

        {/* Mobile Search Section (Only shown on mobile) */}
        <form
          onSubmit={handleSearch}
          className="block md:hidden bg-white rounded-xl shadow-md p-1 mb-8 w-full border border-gray-150"
        >
          <div className="flex items-center w-full gap-1">
            {/* State Select */}
            <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
              <select
                value={selectedState}
                onChange={handleStateChange}
                className="w-full bg-transparent pl-2 pr-5 py-2 text-[11px] text-gray-800 focus:outline-none appearance-none cursor-pointer font-medium"
              >
                <option value="">State</option>
                {states.map(state => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              <ChevronDown size={12} className="text-gray-400 absolute right-1 pointer-events-none" />
            </div>

            {/* City Select */}
            <div className="flex-1 min-w-0 relative flex items-center border-r border-gray-150">
              <select
                value={selectedCity}
                onChange={handleCityChange}
                className="w-full bg-transparent pl-2 pr-5 py-2 text-[11px] text-gray-800 focus:outline-none appearance-none cursor-pointer font-medium"
              >
                <option value="">City</option>
                {availableCities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
              <ChevronDown size={12} className="text-gray-400 absolute right-1 pointer-events-none" />
            </div>

            {/* Category Select */}
            <div className="flex-[1.2] min-w-0 relative flex items-center pr-1">
              <select
                value={selectedCategory}
                onChange={(e) => handleCategoryChange(e.target.value)}
                className="w-full bg-transparent pl-2 pr-5 py-2 text-[11px] text-gray-800 focus:outline-none appearance-none cursor-pointer font-medium"
              >
                <option value="">Category</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
              <ChevronDown size={12} className="text-gray-400 absolute right-1 pointer-events-none" />
            </div>

            {/* Search Button */}
            <button
              type="submit"
              className="bg-primary text-white rounded-lg hover:bg-opacity-90 transition-all flex items-center justify-center w-9 h-9 flex-shrink-0 cursor-pointer"
            >
              <Search size={14} strokeWidth={2.5} />
            </button>
          </div>
        </form>



        {/* Full-width Category Tags or Brands Filter Panel */}
        {displayedCategory && (categoryTags.length > 0 || categoryBrands.length > 0) && (
          <div className="w-full mb-8 flex flex-col gap-4 animate-in fade-in duration-200 text-left">
            {/* Tab Headers */}
            <div className="flex border-b border-gray-200 gap-6">
              {categoryTags.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowBrandsFilter(false)}
                  className={`pb-2 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
                    !showBrandsFilter
                      ? 'border-[#2ab391] text-[#2ab391]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Tags
                </button>
              )}
              {categoryBrands.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowBrandsFilter(true)}
                  className={`pb-2 text-sm font-semibold border-b-2 transition-all cursor-pointer ${
                    showBrandsFilter
                      ? 'border-[#2ab391] text-[#2ab391]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Brands
                </button>
              )}
            </div>

            {/* Render Brands list if active */}
            {showBrandsFilter ? (
              categoryBrands.length > 0 && (
                <div
                  className="flex flex-wrap gap-2.5 justify-start overflow-y-auto max-h-[82px] pr-2 items-center"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <button
                    onClick={() => handleBrandClick('')}
                    className={`px-4 py-1.5 rounded-full text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent ${
                      selectedBrands.length === 0
                        ? 'border-[#2ab391] text-[#2ab391] font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    All Brands
                  </button>
                  {categoryBrands.map(brand => {
                    const isSelected = selectedBrands.includes(String(brand.id));
                    return (
                      <button
                        key={brand.id}
                        onClick={() => handleBrandClick(brand.id)}
                        className={`px-4 py-1.5 rounded-full text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent ${
                          isSelected
                            ? 'border-[#2ab391] text-[#2ab391] font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                        }`}
                      >
                        {brand.name}
                      </button>
                    );
                  })}
                </div>
              )
            ) : (
              /* Render Tags list if active */
              categoryTags.length > 0 && (
                <div
                  className="flex flex-wrap gap-2.5 justify-start overflow-y-auto max-h-[82px] pr-2 items-center"
                  style={{ scrollbarWidth: 'thin' }}
                >
                  <button
                    onClick={() => handleTagClick('')}
                    className={`px-4 py-1.5 rounded-full text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent ${
                      selectedTags.length === 0
                        ? 'border-[#2ab391] text-[#2ab391] font-medium'
                        : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                    }`}
                  >
                    All Tags
                  </button>
                  {categoryTags.map(tag => {
                    const isSelected = selectedTags.includes(String(tag.id));
                    return (
                      <button
                        key={tag.id}
                        onClick={() => handleTagClick(tag.id)}
                        className={`px-4 py-1.5 rounded-full text-xs font-normal transition-all duration-200 border cursor-pointer bg-transparent ${
                          isSelected
                            ? 'border-[#2ab391] text-[#2ab391] font-medium'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:text-gray-900'
                        }`}
                      >
                        {tag.name}
                      </button>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}

        {/* Directory Controls */}
        <div className="flex justify-between items-center mb-6">
          <div className="text-sm font-semibold text-gray-700">
            {totalItems} Results
          </div>

          {/* View Toggles */}
          <div className="hidden md:flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg border transition-colors ${viewMode === 'grid' ? 'bg-[#e0f7f8] border-[#2ab391] text-[#2ab391]' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              title="Grid View"
            >
              <Grid size={18} />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg border transition-colors ${viewMode === 'list' ? 'bg-[#e0f7f8] border-[#2ab391] text-[#2ab391]' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              title="List View"
            >
              <List size={18} />
            </button>
          </div>
        </div>

        {/* Listings Display */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2ab391]"></div>
          </div>
        ) : listings.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center shadow-sm">
            <div className="w-16 h-16 bg-[#eaf7f4] text-[#2ab391] rounded-full flex items-center justify-center mx-auto mb-4">
              <Search size={28} />
            </div>
            <p className="text-gray-500 text-sm max-w-sm mx-auto">
              Our vendors are on the way to serve you
              <br />
              Please visit us after 7 days & we are ready to serve you.
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View Layout */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map(listing => {
              const displayTags = listing.tags || [];
              const showTags = displayTags.slice(0, 3);
              const extraCount = displayTags.length - showTags.length;

              return (
                <div
                  key={listing.id}
                  onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col hover:shadow-md transition-shadow cursor-pointer"
                >
                  {/* Banner Image Area */}
                  <div
                    className="h-48 w-full bg-white relative hover:opacity-95 transition-opacity"
                  >
                    {listing.profileBannerUrl ? (
                      <img
                        src={getImageUrl(listing.profileBannerUrl)}
                        alt={listing.companyName}
                        className="w-full h-full object-contain bg-white"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-[#b0e0e6] text-[#2ab391] font-bold text-xl">
                        {listing.companyName.charAt(0)}
                      </div>
                    )}
                    {/* Status / Category tag overlay */}
                    {listing.membershipPlan === 'PREMIUM' && listing.membershipStatus === 'ACTIVE' && (
                      <span className="absolute top-4 right-4 bg-white/95 backdrop-blur-sm text-[#2ab391] text-[11px] font-bold px-3 py-1 rounded-full shadow-sm">
                        Trusted Vendor
                      </span>
                    )}
                  </div>

                  {/* Card Main Body */}
                  <div className="p-5 flex-1 flex flex-col justify-between">
                    <div>
                      {/* Company Name */}
                      <h4 className="font-bold text-gray-800 text-lg mb-1 leading-snug line-clamp-1">
                        {listing.companyName}
                      </h4>

                      {/* Location details */}
                      <div className="flex items-center gap-1.5 text-gray-500 text-xs mb-3">
                        <MapPin size={13} className="text-[#2ab391]" />
                        <span>{listing.area ? `${listing.area}, ` : ''}{listing.city ? `${listing.city}, ` : ''}{listing.state}</span>
                      </div>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-1.5 mb-4">
                        {showTags.map(tag => (
                          <span key={tag.id} className="bg-gray-50 text-gray-600 text-[11px] font-medium px-2.5 py-1 rounded-md border border-gray-100">
                            {tag.name}
                          </span>
                        ))}
                        {extraCount > 0 && (
                          <span className="bg-[#e0f7f8] text-[#2ab391] text-[11px] font-medium px-2.5 py-1 rounded-md">
                            +{extraCount} more
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Footer Contact Row */}
                    <div className="border-t border-gray-100 pt-4 flex items-center justify-between gap-2 mt-2">
                      {(listing.membershipPlan === 'PROFESSIONAL' || listing.membershipPlan === 'PREMIUM') && listing.membershipStatus === 'ACTIVE' ? (
                        <div className="flex gap-2">
                          {/* Phone call button */}
                          {listing.phone ? (
                            <a
                              href={`tel:${listing.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-xs font-semibold px-3 py-2 rounded-lg transition-colors shadow-sm"
                            >
                              <Phone size={12} />
                              <span>{listing.phone}</span>
                            </a>
                          ) : (
                            <div className="text-gray-400 text-xs">No Phone</div>
                          )}

                          {/* WhatsApp click integration */}
                          {listing.phone && (
                            <a
                              href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold px-3 py-2 rounded-lg transition-all"
                            >
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" className="text-[#25D366]">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                              </svg>
                              <span>WhatsApp</span>
                            </a>
                          )}
                        </div>
                      ) : null}

                      {/* View Details Link */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/listing/${slugify(listing.companyName)}`);
                        }}
                        className="bg-gray-900 hover:bg-gray-800 text-white p-2 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                        title="View Details"
                      >
                        <ArrowUpRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* List View Layout */
          <div className="flex flex-col gap-4">
            {listings.map(listing => {
              const displayTags = listing.tags || [];
              const showTags = displayTags.slice(0, 4);
              const extraCount = displayTags.length - showTags.length;

              return (
                <div
                  key={listing.id}
                  onClick={() => navigate(`/listing/${slugify(listing.companyName)}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 flex flex-col md:flex-row hover:shadow-md transition-shadow cursor-pointer"
                >
                  {/* Left image section */}
                  <div
                    className="w-full md:w-64 bg-white flex-shrink-0 relative hover:opacity-95 transition-opacity"
                  >
                    {listing.profileBannerUrl ? (
                      <img
                        src={getImageUrl(listing.profileBannerUrl)}
                        alt={listing.companyName}
                        className="w-full h-full min-h-[160px] object-contain bg-white"
                      />
                    ) : (
                      <div className="w-full h-full min-h-[160px] flex items-center justify-center bg-[#b0e0e6] text-[#2ab391] font-bold text-xl">
                        {listing.companyName.charAt(0)}
                      </div>
                    )}
                    {listing.membershipPlan === 'PREMIUM' && listing.membershipStatus === 'ACTIVE' && (
                      <span className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm text-[#2ab391] text-[11px] font-bold px-3 py-1 rounded-full shadow-sm">
                        Trusted Vendor
                      </span>
                    )}
                  </div>

                  {/* Right Details section */}
                  <div className="p-6 flex-grow flex flex-col justify-between">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-2">
                      <div>
                        {/* Company Name */}
                        <h4 className="font-bold text-gray-800 text-lg mb-1 leading-snug">
                          {listing.companyName}
                        </h4>
                        {/* Location */}
                        <div className="flex items-center gap-1.5 text-gray-500 text-xs">
                          <MapPin size={13} className="text-[#2ab391]" />
                           <span>{listing.area ? `${listing.area}, ` : ''}{listing.city ? `${listing.city}, ` : ''}{listing.state}</span>
                        </div>
                      </div>
                    </div>

                    {/* Tags row */}
                    <div className="flex flex-wrap gap-1.5 mb-4 mt-2">
                      {showTags.map(tag => (
                        <span key={tag.id} className="bg-gray-50 text-gray-600 text-[11px] font-medium px-2.5 py-1 rounded-md border border-gray-100">
                          {tag.name}
                        </span>
                      ))}
                      {extraCount > 0 && (
                        <span className="bg-[#e0f7f8] text-[#2ab391] text-[11px] font-medium px-2.5 py-1 rounded-md">
                          +{extraCount} more
                        </span>
                      )}
                    </div>

                    {/* Bottom Actions section */}
                    <div className="border-t border-gray-100 pt-4 flex items-center justify-between gap-2">
                      {(listing.membershipPlan === 'PROFESSIONAL' || listing.membershipPlan === 'PREMIUM') && listing.membershipStatus === 'ACTIVE' ? (
                        <div className="flex items-center gap-3">
                          {/* Contact Call Link */}
                          {listing.phone ? (
                            <a
                              href={`tel:${listing.phone}`}
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 bg-[#12B76A] hover:bg-[#0fa15c] text-white text-xs font-semibold px-4 py-2.5 rounded-lg transition-colors shadow-sm"
                            >
                              <Phone size={12} />
                              <span>{listing.phone}</span>
                            </a>
                          ) : (
                            <div className="text-gray-400 text-xs">No Phone</div>
                          )}

                          {/* WhatsApp integrate */}
                          {listing.phone && (
                            <a
                              href={`https://wa.me/91${listing.phone.replace(/[^0-9]/g, '')}`}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="flex items-center gap-1.5 border border-gray-200 hover:bg-gray-50 text-gray-600 text-xs font-semibold px-4 py-2.5 rounded-lg transition-all"
                            >
                              <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" className="text-[#25D366]">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.335-1.662c1.746.953 3.71 1.458 5.704 1.459h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                              </svg>
                              <span>WhatsApp</span>
                            </a>
                          )}
                        </div>
                      ) : null}

                      {/* Arrow link */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/listing/${slugify(listing.companyName)}`);
                        }}
                        className="bg-gray-900 hover:bg-gray-800 text-white p-2.5 rounded-lg transition-colors flex items-center justify-center cursor-pointer"
                        title="View Details"
                      >
                        <ArrowUpRight size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer controls: pagination entries text, display limit, and links */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between text-xs text-gray-500 font-medium border-t border-gray-100 pt-5 mt-10">
          {/* Left: Entries Info */}
          <div className="text-gray-500 text-xs font-semibold text-center md:text-left w-full md:w-auto">
            {totalItems === 0 ? (
              'No records available'
            ) : (
              `Showing ${currentPage * pageSize + 1} to ${Math.min((currentPage + 1) * pageSize, totalItems)} of ${totalItems} entries`
            )}
          </div>

          {/* Right: Rows per page and Page buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6 w-full md:w-auto">
            {/* Rows per page Selector */}
            <div className="flex items-center justify-center gap-2 w-full sm:w-auto">
              <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Show</span>
              <div className="relative">
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setCurrentPage(0);
                  }}
                  className="w-16 bg-white border border-gray-200 rounded-xl pl-3 pr-8 py-1.5 text-xs font-bold text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#2ab391] focus:border-[#2ab391] appearance-none cursor-pointer shadow-xs"
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>
                <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                  <ChevronDown size={12} />
                </span>
              </div>
              <span className="text-xs text-gray-500 font-bold uppercase tracking-wider">Entries</span>
            </div>

            {/* Pagination Buttons */}
            {totalPages > 0 && (
              <div className="flex items-center justify-between sm:justify-start gap-1 w-full sm:w-auto">
                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 0}
                  className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                >
                  Previous
                </button>

                {/* Page numbers - Hidden on mobile, visible on sm and larger */}
                <div className="hidden sm:flex items-center gap-1">
                  {getPageNumbers().map((pageNum, idx) => {
                    if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                      return (
                        <span key={`ell-${idx}`} className="px-2 py-1.5 text-gray-400 font-bold">
                          ...
                        </span>
                      );
                    }
                    const isActive = currentPage === pageNum;
                    return (
                      <button
                        key={`page-${pageNum}`}
                        type="button"
                        onClick={() => handlePageChange(pageNum)}
                        className={`min-w-8 h-8 px-2.5 flex items-center justify-center rounded-xl text-xs font-bold border transition-all cursor-pointer ${isActive
                          ? 'bg-[#2ab391] border-[#2ab391] text-white shadow-sm'
                          : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        {pageNum + 1}
                      </button>
                    );
                  })}
                </div>

                {/* Page indicator text - Visible only on mobile */}
                <span className="sm:hidden text-xs text-gray-500 font-bold px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl shadow-xs min-w-[70px] text-center">
                  {currentPage + 1} / {totalPages}
                </span>

                <button
                  type="button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages - 1}
                  className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-xl font-bold hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all cursor-pointer shadow-xs flex-1 sm:flex-initial text-center"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default Listing;
